<script lang="ts">
    import { PlusCircleIcon, MinusCircleIcon, XCircleIcon, ExternalLinkIcon } from "svelte-feather-icons";
    import { fade, scale } from "svelte/transition";
    import SvelteTooltip from "svelte-tooltip";
    import {
        calculateProsCons,
        formatMatchPercentage,
        formatMatchPercentageTooltip,
        formatNumSlopes,
        formatURL,
        SlopeColor,
    } from "../formatting";
    import BlackSquare from "../icons/BlackSquare.svelte";
    import BlueSquare from "../icons/BlueSquare.svelte";
    import GreenCircle from "../icons/GreenCircle.svelte";
    import type { AnsweredQuestions } from "../questions";
    import type { TransportMode } from "../transport_mode.js";
    import { link } from "../routing";

    import { QuantizedScore, SearchResult } from "../search";
    import Map from "./Map.svelte";
    import CrossCountry from "../icons/CrossCountry.svelte";

    export let location: SearchResult;
    export let origin_id: number | null;
    export let origin_name: string | null;
    export let transport_mode: TransportMode | null;
    export let answered: AnsweredQuestions;
    export let urlParams: URLSearchParams;
    export let fullscreen: boolean;
    export let onClose: (() => void) | null = null;
    let mapComponent: Map | null = null;
    const resizeMap = () => {
        if (mapComponent) {
            mapComponent.resize();
        }
    };

    let description = "";
    $: {
        let paragraphs = ["<i>Ingen beskrivning</i>"];
        if (location.long_description) {
            paragraphs = location.long_description.split("\n");
        } else if (location.description) {
            paragraphs = location.description.split("\n");
        }
        description = paragraphs.map((p) => `<p>${p}</p>`).join("");
    }
</script>

{#if !fullscreen}
    <div
        class="white-backdrop"
        in:fade={{ duration: 300 }}
        out:fade={{ duration: 300 }}
        on:click={() => {
            if (onClose) {
                onClose();
            }
        }}
    />
{/if}
<article
    class="resort-details{fullscreen ? ' fullscreen' : ''}"
    in:fade={{ duration: fullscreen ? 0 : 300 }}
    out:fade={{ duration: 300 }}
>
    <div class="item-header">
        <h1>{location.name}</h1>
        {#if !fullscreen}
            <span class="match-score-container">
                <SvelteTooltip tip={formatMatchPercentageTooltip(location.matching_percentage)} bottom color="#2289d3">
                    <span class="match-score">{formatMatchPercentage(location.matching_percentage)}</span>
                </SvelteTooltip>
            </span>
        {/if}
    </div>
    <div class="left-column">
        {#if location.image_url}
            {#if location.image_attribution}
                <SvelteTooltip tip={location.image_attribution} top color="#2289d3">
                    <!-- svelte-ignore a11y-missing-attribute -->
                    <img class="header-image" src={location.image_url} on:load={resizeMap} />
                </SvelteTooltip>
            {:else}
                <!-- svelte-ignore a11y-missing-attribute -->
                <img class="header-image" src={location.image_url} on:load={resizeMap} />
            {/if}
        {/if}
        {@html description}
        {#if location.system_name}
            <div class="area-info">
                Skidanläggningar i {location.system_name}
                <div class="resorts-in-area">
                    {#each location.resorts_in_same_system as resort}
                        <a
                            href="/skidort/{resort.id}{answered ? '?' + urlParams.toString() : ''}"
                            class="resort-in-area-link"
                            use:link>{resort.name}</a
                        >
                    {/each}
                </div>
            </div>
        {/if}
        <hr />
        <div class="middle-info">
            <div class="slope-counts">
                <span class="slope-count slope-green"
                    ><GreenCircle />
                    {formatNumSlopes(location.num_green_runs, SlopeColor.Green)}
                </span>
                <span class="slope-count slope-blue"
                    ><BlueSquare />
                    {formatNumSlopes(location.num_blue_runs, SlopeColor.Blue)}
                </span>
                <span class="slope-count slope-red"
                    ><BlueSquare />
                    {formatNumSlopes(location.num_red_runs, SlopeColor.Red)}
                </span>
                <span class="slope-count slope-black"
                    ><BlackSquare />
                    {formatNumSlopes(location.num_black_runs, SlopeColor.Black)}
                </span>
                {#if location.num_unpisted_runs}
                    <span class="slope-count slope-yellow"
                        ><BlackSquare />
                        {formatNumSlopes(location.num_unpisted_runs, SlopeColor.Unpisted)}
                    </span>
                {/if}
                {#if location.crosscountry_length_km}
                    <span class="slope-count slope-black"
                        ><CrossCountry />
                        {`${location.crosscountry_length_km} km längdskidsspår`}
                    </span>
                {/if}
            </div>
        </div>
        <ul class="pros-cons">
            {#each calculateProsCons(location, null) as item}
                {#if item.score == QuantizedScore.GOOD || item.score == QuantizedScore.GREAT}
                    <li class="positive">
                        <PlusCircleIcon />
                        <span>{item.description}</span>
                    </li>
                {/if}
            {/each}
        </ul>
        {#if location.website}
            <a href={location.website} class="resort-url">
                <ExternalLinkIcon />
                <span>{formatURL(location.website)}</span>
            </a>
        {/if}
        {#if location.piste_map_image_url}
            <a href={location.piste_map_image_url}>
                <img
                    class="piste-map"
                    src={location.piste_map_image_url}
                    on:load={resizeMap}
                    alt="Pistkarta {location.name}"
                />
            </a>
        {/if}
    </div>
    <Map {origin_id} {origin_name} resort={location} {transport_mode} bind:this={mapComponent} />
    {#if !fullscreen}
        <div
            class="close-icon"
            in:fade={{ duration: 300 }}
            out:fade={{ duration: 300 }}
            on:click={() => {
                if (onClose) {
                    onClose();
                }
            }}
        >
            <XCircleIcon />
        </div>
    {/if}
</article>

<style>
</style>
